/**
 * Lazy-loaded Mixpanel implementation that defers 306KB library loading
 * until it's actually needed (production environment only)
 * This reduces initial bundle size by ~306KB for non-production builds
 */

import { PostgrestClient } from "@supabase/postgrest-js";

// Extend mixpanel types to include our custom properties
declare module "mixpanel-browser" {
  interface OverridedMixpanel {
    _track?: any;
    getSessionState?: () => SessionState;
  }
}

interface SessionState {
  sessionId: string | null;
  lastActivity: number;
  fboId: string | null;
}

const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

const sessionState: SessionState = {
  sessionId: null,
  lastActivity: 0,
  fboId: null,
};

// Lazy loader for Mixpanel
let mixpanelPromise: Promise<any> | null = null;
let loadedMixpanel: any = null;

const loadMixpanel = async () => {
  if (!mixpanelPromise) {
    mixpanelPromise = import("mixpanel-browser").then(
      (module) => module.default
    );
  }
  if (!loadedMixpanel) {
    const mixpanel = await mixpanelPromise;

    // Initialize Mixpanel for production
    mixpanel.init("62aa25aa93870b1f6f43b1e11def428b", {
      debug: false,
      record_sessions_percent: 100,
      record_mask_text_selector: "#noop",
    });

    loadedMixpanel = mixpanel;
  }
  return loadedMixpanel;
};

// Sanitize error messages to remove sensitive information
function sanitizeError(error: any): string {
  if (!error) return "Unknown error";

  // Convert error to string
  const errorString = error instanceof Error ? error.message : String(error);

  // Remove potential sensitive patterns
  return errorString
    .replace(/session_id:\s*[\w-]+/gi, "session_id: [REDACTED]")
    .replace(/"p_session_id":\s*"[^"]+"/gi, '"p_session_id": "[REDACTED]"')
    .replace(/token:\s*[\w.-]+/gi, "token: [REDACTED]")
    .replace(/Bearer\s+[\w.-]+/gi, "Bearer [REDACTED]")
    .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, "[EMAIL]")
    .replace(/\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g, "[CARD]");
}

// Dummy implementation for development
const dummy: Record<string, any> = new Proxy(
  {},
  {
    get(target, propKey) {
      if (propKey.toString() === "people") {
        return dummy;
      }
      if (propKey.toString() === "identify") {
        return (userId: string) => {
          console.info(`Mixpanel Dev: identify => ${JSON.stringify(userId)}`);
        };
      }
      if (propKey.toString() === "register") {
        return (properties: any) => {
          console.info(
            `Mixpanel Dev: register => ${JSON.stringify(properties)}`
          );
        };
      }
      if (propKey.toString() === "reset") {
        return () => {
          console.info(`Mixpanel Dev: reset`);
        };
      }
      if (propKey.toString() === "alias") {
        return (alias: string, original?: string) => {
          console.info(
            `Mixpanel Dev: alias => ${JSON.stringify({ alias, original })}`
          );
        };
      }
      if (propKey.toString() === "get_distinct_id") {
        return () => {
          const mockId = "dev-user-123";
          console.info(`Mixpanel Dev: get_distinct_id => ${mockId}`);
          return mockId;
        };
      }
      if (
        propKey.toString() === "opt_out_tracking" ||
        propKey.toString() === "opt_in_tracking"
      ) {
        return () => {
          console.info(`Mixpanel Dev: ${propKey.toString()}`);
        };
      }
      return (...args: any[]) => {
        // Format the output to match the expected format (single arg without array brackets)
        const formattedArgs =
          args.length === 1 ? JSON.stringify(args[0]) : JSON.stringify(args);
        console.info(
          `Mixpanel Dev: called with ${propKey.toString()} => ${formattedArgs}`
        );
      };
    },
  }
);

// Session tracking functions
async function recordSession(
  postgrest: PostgrestClient,
  fboId: string
): Promise<string | null> {
  try {
    const { data, error } = await postgrest.rpc("record_session", {
      p_fbo_id: fboId,
    });

    if (error) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Error recording session:", sanitizeError(error));
      }
      return null;
    }

    return data?.[0] || null;
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.error("Error recording session:", sanitizeError(error));
    }
    return null;
  }
}

async function checkAndRecordSession(
  postgrest: PostgrestClient,
  userId: string,
  fboId: string,
  mixpanelTracker: any
): Promise<void> {
  const now = Date.now();

  // Check if we need to create a new session
  const needsNewSession =
    !sessionState.sessionId ||
    sessionState.fboId !== fboId ||
    now - sessionState.lastActivity > SESSION_TIMEOUT;

  if (needsNewSession) {
    const sessionId = await recordSession(postgrest, fboId);

    if (sessionId) {
      sessionState.sessionId = sessionId;
      sessionState.fboId = fboId;
      sessionState.lastActivity = now;

      // Track session start in Mixpanel
      mixpanelTracker.track("Session Started", {
        session_id: sessionId,
        user_id: userId,
        fbo_id: fboId,
      });
    }
  } else {
    // Update last activity time
    sessionState.lastActivity = now;
  }
}

// Create mixpanel wrapper that lazy-loads only for production
const createMixpanelWrapper = () => {
  const isProduction = window.location.hostname === "app.airplx.com";

  if (!isProduction) {
    // Return dummy immediately for non-production
    return Promise.resolve(dummy);
  }

  // For production, return a promise that resolves to the lazy-loaded instance
  return loadMixpanel().then((mixpanel) => {
    return {
      ...mixpanel,
      _track: mixpanel.track,
      track: async (eventName: string, properties?: any) => {
        // Call original track function
        mixpanel.track(eventName, properties);

        // Get session data from the app context
        const sessionData = (window as any).__airplxSessionData;

        if (sessionData && sessionData.userId && sessionData.fboId) {
          // Fire and forget - don't block UI
          checkAndRecordSession(
            sessionData.postgrest,
            sessionData.userId,
            sessionData.fboId,
            mixpanel
          ).catch((err) => {
            if (process.env.NODE_ENV !== "production") {
              console.error("Session tracking error:", sanitizeError(err));
            }
          });
        }
      },
      identify: mixpanel.identify.bind(mixpanel),
      register: mixpanel.register.bind(mixpanel),
      reset: mixpanel.reset ? mixpanel.reset.bind(mixpanel) : () => {},
      alias: mixpanel.alias ? mixpanel.alias.bind(mixpanel) : () => {},
      get_distinct_id: mixpanel.get_distinct_id
        ? mixpanel.get_distinct_id.bind(mixpanel)
        : () => "unknown",
      opt_out_tracking: mixpanel.opt_out_tracking
        ? mixpanel.opt_out_tracking.bind(mixpanel)
        : () => {},
      opt_in_tracking: mixpanel.opt_in_tracking
        ? mixpanel.opt_in_tracking.bind(mixpanel)
        : () => {},
      people: mixpanel.people,
      getSessionState: () => ({ ...sessionState }),
    };
  });
};

// Create the promise-based mixpanel instance
const mixpanelInstance = createMixpanelWrapper();

// Export as named export
export { mixpanelInstance };

// Session management functions remain the same but work with the lazy instance
export async function setupSessionTracking(
  postgrest: PostgrestClient,
  userId: string,
  fboId: string
) {
  (window as any).__airplxSessionData = { postgrest, userId, fboId };

  // Start session heartbeat to keep sessions active
  startSessionHeartbeat();
}

export function cleanupSessionTracking() {
  stopSessionHeartbeat();

  // Clear global session data
  if (typeof window !== "undefined") {
    (window as any).__airplxSessionData = null;
  }
}

export async function ensureSession(
  postgrest: PostgrestClient,
  userId: string,
  fboId: string
): Promise<void> {
  const mixpanel = await mixpanelInstance;
  await checkAndRecordSession(postgrest, userId, fboId, mixpanel);
}

export async function endAllSessions(
  postgrest: PostgrestClient
): Promise<void> {
  try {
    await postgrest.rpc("end_session", {});

    // Clear session state
    sessionState.sessionId = null;
    sessionState.lastActivity = 0;
    sessionState.fboId = null;

    // Stop session heartbeat
    stopSessionHeartbeat();

    // Clear global session data
    if (typeof window !== "undefined") {
      (window as any).__airplxSessionData = null;
    }
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.error("Error ending all sessions:", sanitizeError(error));
    }
  }
}

// Session heartbeat management
let heartbeatInterval: NodeJS.Timeout | null = null;

function startSessionHeartbeat() {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
  }

  heartbeatInterval = setInterval(async () => {
    const sessionData = (window as any).__airplxSessionData;
    if (
      sessionData &&
      sessionData.userId &&
      sessionData.fboId &&
      sessionState.sessionId
    ) {
      const now = Date.now();
      const timeSinceLastActivity = now - sessionState.lastActivity;

      // Only send heartbeat if there's been recent activity (within 10 minutes)
      if (timeSinceLastActivity < 10 * 60 * 1000) {
        const mixpanel = await mixpanelInstance;
        await checkAndRecordSession(
          sessionData.postgrest,
          sessionData.userId,
          sessionData.fboId,
          mixpanel
        );
      }
    }
  }, 5 * 60 * 1000); // Check every 5 minutes
}

function stopSessionHeartbeat() {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
}

// Browser event listeners
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", async () => {
    const sessionData = (window as any).__airplxSessionData;
    if (sessionData && sessionData.postgrest && sessionState.sessionId) {
      try {
        stopSessionHeartbeat();

        await sessionData.postgrest.rpc("end_session", {
          p_session_id: sessionState.sessionId,
        });
      } catch (error) {
        if (process.env.NODE_ENV !== "production") {
          console.error(
            "Error ending session on page unload:",
            sanitizeError(error)
          );
        }
      }
    }
  });

  document.addEventListener("visibilitychange", async () => {
    if (document.visibilityState === "visible") {
      const sessionData = (window as any).__airplxSessionData;
      if (sessionData && sessionData.userId && sessionData.fboId) {
        startSessionHeartbeat();

        const mixpanel = await mixpanelInstance;
        await checkAndRecordSession(
          sessionData.postgrest,
          sessionData.userId,
          sessionData.fboId,
          mixpanel
        );
      }
    } else {
      stopSessionHeartbeat();
    }
  });
}

// Default export for backwards compatibility
export default mixpanelInstance;
